import { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import { otherGroupsRegistrationFormSchema } from '../components/registrationFormComponents/schema';

import { AppContext } from '../contexts/app.context';
import { Button, Input, Loader, Text } from '../components';
import {
  EstablishmentDetailsForm,
  ClassDetailsForm,
  EmailConsentForm,
  InchargePersonForm,
  TotalStudentDisplay,
} from '../components/registrationFormComponents';
import {
  FINAL_BOOKING_TIME,
  FINAL_EDIT_TIME,
  otherGroupsFormEstablishmentTypes,
} from '../components/registrationFormComponents/data';
import { otherGroupsRegistration, updateDashboardDetails } from '../service';
import { toast } from 'react-toastify';
import { scrollToClass, submitHelper } from '../components/registrationFormComponents/util';

const translationPrefix = 'otherGroupsRegistrationForm.form';

const classInitialValues = {
  name: '',
  level: '',
  numberOfStudents: '',
  sector: '',
  visitDay: '',
  arrivalTime: '',
  departureTime: '',
  modeOfTransport: '',
  otherModeOfTransport: '',
  picnicOnSite: 'false',
  picnicTimeSlot: '',
  handicap: 'false',
  handicapDescription: '',
  responsibleInformation: {
    firstName: '',
    lastName: '',
    function: '',
    email: '',
    phone: '',
  },
};

const OtherGroupsRegistrationForm = ({ storedData, refresh, refreshing }) => {
  const { t } = useTranslation();
  const { admin, setBaseContainerConfig } = useContext(AppContext);

  const { id } = useParams();

  const [otherGroupType, setOtherGroupType] = useState('other-school');
  const [saving, setSaving] = useState(false);
  const [openClassIndex, setOpenClassIndex] = useState(null);
  const [experienceInteractionConfig, setExperienceInteractionConfig] = useState({
    visible: true,
    editable: true,
    newBooking: true,
    finalBookingTimePassed: false,
    slotEditTimePassed: false,
  });
  const formik = useFormik({
    initialValues: {
      establishmentDetails: {},
      inchargePersonDetails: {},
      inchargePersonAnother: 'true',
      inchargePersonAnotherDetails: {},
      classes: [
        {
          ...classInitialValues,
        },
      ],
      emailConsent: {
        emailConsent: 'true',
        studentsSafetyConsent: 'false',
        email: '',
      },
    },
    validationSchema: otherGroupsRegistrationFormSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values) => {
      try {
        if (!storedData && experienceInteractionConfig.finalBookingTimePassed) {
          toast.error(t(`${translationPrefix}.popupError.bookingTimePassed`));
          return;
        }

        const emailConsentGiven = values?.emailConsent?.emailConsent;
        const emailConsentEmail = values?.emailConsent?.email;
        const inchargePersonEmail = values?.inchargePersonDetails?.email;

        if (emailConsentGiven === 'true' && emailConsentEmail !== inchargePersonEmail) {
          toast.error(t('otherGroupsRegistrationForm.popupError.emailConsentEmail'));
          return;
        }

        setSaving(true);
        !storedData
          ? await otherGroupsRegistration(values)
          : await updateDashboardDetails({ ...values, formType: 'other-group' });
        toast.success(t('otherGroupsRegistrationForm.success'));

        if (refresh) {
          refresh();
        }
      } catch (error) {
        console.log(error);
        if (error?.response?.data?.errors) {
          const errors = Object.keys(error?.response?.data?.errors);
          errors.forEach((error) => {
            toast.error(t(`${translationPrefix}.popupError.${error}`));
          });
        } else {
          toast.error(t('otherGroupsRegistrationForm.error'));
        }
      } finally {
        setSaving(false);
      }
    },
  });

  useEffect(() => {
    if (
      [...otherGroupsFormEstablishmentTypes]
        .slice(0, 2)
        .map((type) => type.value)
        .includes(formik.values.establishmentDetails.type)
    ) {
      setOtherGroupType('other-school');
    } else {
      setOtherGroupType('other-group');
    }
  }, [formik.values.establishmentDetails.type]);

  useEffect(() => {
    if (!storedData) return;
    formik.setValues(storedData);
  }, [storedData]);

  useEffect(() => {
    const finalBookingTimePassed = new Date() >= FINAL_BOOKING_TIME;

    setExperienceInteractionConfig((prev) => ({
      ...(finalBookingTimePassed
        ? {
            visible: storedData?.classes?.length > 0,
            editable: admin?.specialPermissions?.includes('edit'),
            newBooking: storedData ? Object.keys(storedData)?.length === 0 : true,
            finalBookingTimePassed,
          }
        : prev),
      slotEditTimePassed: !(id && admin) && new Date() >= FINAL_EDIT_TIME,
    }));
  }, [storedData, admin, id]);

  useEffect(() => {
    setBaseContainerConfig({
      heading: t('otherGroupsRegistrationForm.header.heading'),
      subHeading: t('otherGroupsRegistrationForm.header.subHeading'),
      buttonText: t('loginButtonText'),
      buttonURL: '/login',
    });
  }, []);

  return (
    <>
      <div className="text-left">
        <Text type={'big'} text={t('otherGroupsRegistrationForm.heading')} />
      </div>

      <form
        onSubmit={(e) => {
          submitHelper(e, formik, t);
        }}>
        <div className="w-full mt-12">
          <Text
            type={'small'}
            text={t(`${translationPrefix}.heading`)}
            className={
              'bg-teal-20 text-center font-poppins font-bold text-white p-2 w-full rounded-md mb-2'
            }
          />

          <div className="bg-teal-10 rounded-lg p-4 sm:px-10 pt-10">
            <EstablishmentDetailsForm
              type={'other-group'}
              formik={formik}
              translationPrefix={translationPrefix}
            />

            <span className="block my-10 bg-black h-[0.5px] w-full" />

            <Text
              type={'small'}
              text={t(`${translationPrefix}.inchargePerson.heading`)}
              className={'text-center mb-6 font-semibold'}
            />

            <InchargePersonForm formik={formik} translationPrefix={translationPrefix} />

            <span className="block my-10 bg-black h-[0.5px] w-full" />

            <Text
              type={'small'}
              text={t(`${translationPrefix}.inchargePersonAnother.heading`)}
              className={'text-center mb-6 font-semibold'}
            />

            <Input
              name={'inchargePersonAnother'}
              id={'inchargePersonAnother'}
              paragraphLabel={t(`${translationPrefix}.inchargePersonAnother.label`)}
              paragraphLabelClassName="text-center"
              radioClassName="justify-center"
              type={'radio'}
              options={[
                {
                  value: 'false',
                  label: t(`${translationPrefix}.inchargePersonAnother.false`),
                },
                {
                  value: 'true',
                  label: t(`${translationPrefix}.inchargePersonAnother.true`),
                },
              ]}
              defaultValue={formik.values?.inchargePersonAnother || 'true'}
              onChange={(value) => {
                formik.setFieldValue('inchargePersonAnother', value);
              }}
            />

            {formik.values?.inchargePersonAnother === 'false' && (
              <>
                <span className="block my-10 w-full" />
                <InchargePersonForm
                  formik={formik}
                  objectName={'inchargePersonAnotherDetails'}
                  translationPrefix={translationPrefix}
                />
              </>
            )}
          </div>
        </div>

        <div className="w-full mt-6">
          <Text
            type={'small'}
            text={t(`${translationPrefix}.visit.heading`)}
            className={
              'bg-yellow-10 text-center font-poppins font-bold text-white p-2 w-full rounded-md mb-2'
            }
          />

          <div className="bg-lightBlue-10 rounded-lg p-4 sm:px-10 pt-10">
            <ClassDetailsForm
              type={otherGroupType}
              formik={formik}
              translationPrefix={translationPrefix}
              openClassIndex={openClassIndex}
              experienceInteractionConfig={experienceInteractionConfig}
            />

            <div className="w-full flex items-center justify-center my-8">
              <Button
                type="button"
                text={t('otherGroupsRegistrationForm.addClassButtonText')}
                className={'bg-transparent border-2 border-navy-10 !text-navy-10 rounded-md'}
                disabled={
                  experienceInteractionConfig.finalBookingTimePassed &&
                  !experienceInteractionConfig.editable
                }
                callback={() => {
                  formik.setFieldValue('classes', [
                    ...formik.values.classes,
                    { ...classInitialValues },
                  ]);
                  setOpenClassIndex(formik.values.classes.length);
                  scrollToClass(`class-${formik.values.classes.length}`);
                }}
              />
            </div>
            <TotalStudentDisplay formik={formik} translationPrefix={translationPrefix} />
          </div>
        </div>

        <div className="w-full mt-6">
          <Text
            type={'medium'}
            text={t(`${translationPrefix}.emailConsent.heading`)}
            className={'mb-6 mt-4'}
          />

          <EmailConsentForm formik={formik} translationPrefix={translationPrefix} />

          <Button
            type="submit"
            text={t('otherGroupsRegistrationForm.submitButtonText')}
            className={'mt-20'}
            loading={formik.isSubmitting}
            disabled={!storedData && experienceInteractionConfig.finalBookingTimePassed}
          />

          <div className="bg-teal-10 rounded-lg p-6 mt-12">
            <Text
              type={'medium'}
              text={t(`${translationPrefix}.support.heading`)}
              className={'text-navy-10'}
            />
            <Text
              type={'small'}
              text={t(`${translationPrefix}.support.paragraph`)}
              className={'mb-2'}
            />
          </div>
          <Text
            type={'small'}
            text={t(`${translationPrefix}.termsAndConditions`)}
            className={'mt-6 text-navy-10 underline'}
          />
        </div>
      </form>

      {(saving || refreshing) && (
        <Loader
          bgColor={'transparent'}
          loaderType={'sync'}
          text={storedData ? t('updating') : t('saving')}
        />
      )}
    </>
  );
};

export default OtherGroupsRegistrationForm;
